/* Lo que se puede decir de una factura que llegó por mail, sin tocar la base.
 *
 * Vive acá y no en el servicio para poder probarlo solo: el servicio importa el
 * cliente de Supabase, y un test que quiera fijar «lo que no se leyó NO se
 * afirma» no tiene por qué levantar una conexión.
 */

export const TIPOS = [
  ["todos", "Todos"],
  ["factura", "Facturas"],
  ["remito", "Remitos"],
  ["presupuesto", "Presupuestos"],
  ["otro", "Sin clasificar"],
];

export const ROTULO = {
  factura: "Factura",
  remito: "Remito",
  presupuesto: "Presupuesto",
  otro: "Sin clasificar",
};

/* Las tres cosas que le pueden haber pasado al papel. NO SON DOS.
 *
 * «Sin leer» y «no pude leer» se dibujan igual si uno las junta —las dos
 * muestran los campos vacíos— y son opuestas: la primera se arregla esperando
 * la próxima pasada, la segunda abriendo el PDF. La base las distingue con
 * `leido_en` y `lectura_error`, y la pantalla también tiene que hacerlo.
 */
export const SIN_LEER = "sin_leer";
export const NO_PUDE = "no_pude";
export const LEIDA = "leida";

export function lectura(fila) {
  if (fila?.lectura_error) return NO_PUDE;
  if (!fila?.leido_en) return SIN_LEER;
  return LEIDA;
}

/* De qué tipo es. LO DICE EL PAPEL; EL ASUNTO ES EL RESPALDO.
 *
 * Hasta que el bot leyó las facturas, lo único que había era el asunto que
 * escribió el que la mandó. Ahora el modelo mira el papel, y lo que dice el
 * papel gana: un mail con asunto «Factura» que trae adentro un remito es un
 * remito.
 *
 * LO QUE NO LO DICE NO SE ADIVINA. Por descarte, un asunto vacío caería en
 * «otro», que se lee como una categoría elegida. «Sin clasificar» es la verdad
 * y manda a abrir el PDF, que es lo único que lo resuelve.
 */
export function tipoDe(fila) {
  const leido = String(fila?.tipo || "").toLowerCase();
  if (ROTULO[leido] && leido !== "otro") return leido;
  const a = String(fila?.asunto || "").toLowerCase();
  if (a.includes("remito")) return "remito";
  if (a.includes("presupuesto")) return "presupuesto";
  if (a.includes("factura")) return "factura";
  return "otro";
}

/* KB/MB, para que se note un adjunto de 3 KB —una firma, no una factura— sin
 * tener que abrirlo. `null` cuando no hay un número: un «0 B» inventado se lee
 * como un archivo vacío, que es otra cosa. */
export function peso(bytes) {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n <= 0) return null;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

/* La clave con la que se agrupa por proveedor.
 *
 * SE LE SACA TODO LO QUE NO ES LETRA O NÚMERO, incluidos los espacios. «Würth
 * Argentina S.A.» y «WURTH ARGENTINA SA» son el mismo proveedor escrito por dos
 * escaneos distintos, y si la puntuación se cambiara por un espacio en vez de
 * borrarse quedarían «WURTH ARGENTINA S A» y «WURTH ARGENTINA SA»: dos grupos,
 * que es justo lo que el filtro tiene que evitar — y es lo que Nico pidió que
 * NO pase cuando dijo «discriminadas las de Menchón».
 *
 * El precio es que dos nombres que sólo se diferencian en dónde cae un espacio
 * caen juntos. En nombres de empresa eso no pasa; el error al revés —el mismo
 * proveedor en dos filas del selector— pasa con cada escaneo nuevo.
 */
export function clave(nombre) {
  return String(nombre || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "");
}

/* Los proveedores que hay para elegir, con cuántas tiene cada uno.
 *
 * SALE DE LAS FILAS Y NO DE UNA LISTA. Una lista escrita a mano se queda corta
 * el día que aparece un proveedor nuevo, y ese proveedor no se puede filtrar
 * justo cuando recién llegó, que es cuando a alguien le importa.
 *
 * Las que todavía no tienen proveedor leído NO entran acá: van a su propia
 * opción, para que «sin leer» no parezca un proveedor.
 */
export function proveedores(filas) {
  const por = new Map();
  for (const f of filas || []) {
    const nombre = f?.proveedor;
    if (!nombre) continue;
    const k = clave(nombre);
    if (!k) continue;
    const y = por.get(k);
    if (y) y.cuantas += 1;
    else por.set(k, { clave: k, rotulo: String(nombre).trim(), cuantas: 1 });
  }
  return [...por.values()].sort((a, b) => a.rotulo.localeCompare(b.rotulo, "es"));
}

/* Quiénes mandaron facturas, con cuántas cada uno.
 *
 * ES EL OTRO EJE, Y PARA «LAS DE MENCHÓN» ES EL QUE SIRVE. En la lista de la
 * empresa, NESTOR MENCHON es TALLER de Río Grande, con su propio mail: no es un
 * proveedor, es el que compra y manda el papel. Una factura suya la va a emitir
 * la casa de repuestos de Río Grande, así que filtrar por `proveedor` no lo
 * encuentra nunca — y filtrar por `dominio` lo mete adentro de «gmail.com»
 * junto con medio mundo.
 *
 * La clave es el mail entero, que es lo único que identifica a una persona sin
 * ambigüedad. El rótulo es el nombre cuando el mail lo trae, porque «Nestor
 * Menchon» se busca y «nestoromarmenchon@gmail.com» se adivina.
 */
export function remitentes(filas) {
  const por = new Map();
  for (const f of filas || []) {
    const mail = String(f?.remitente_email || "").trim().toLowerCase();
    if (!mail) continue;
    const y = por.get(mail);
    if (y) { y.cuantas += 1; continue; }
    const crudo = String(f?.remitente || "").trim();
    const nombre = crudo.replace(/\s*<[^>]*>\s*/, "").replace(/^"|"$/g, "").trim();
    por.set(mail, {
      clave: mail,
      rotulo: nombre && nombre.toLowerCase() !== mail ? `${nombre} · ${mail}` : mail,
      cuantas: 1,
    });
  }
  return [...por.values()].sort((a, b) => a.rotulo.localeCompare(b.rotulo, "es"));
}

/* Si la fila entra en lo que se buscó.
 *
 * Mira TODO lo que identifica un papel: quién lo mandó, quién lo emitió, el
 * CUIT, el número, el asunto, el archivo y el detalle. Escribir «Menchón» tiene
 * que encontrarlo tanto si Menchón lo mandó como si Menchón lo emitió — y
 * cuál de las dos cosas es no lo puede decidir la pantalla.
 */
export function coincide(fila, texto) {
  const t = String(texto || "").trim().toLowerCase();
  if (!t) return true;
  return [
    fila?.remitente, fila?.remitente_email, fila?.dominio, fila?.asunto,
    fila?.nombre, fila?.proveedor, fila?.cuit, fila?.numero, fila?.detalle,
  ].some((v) => String(v || "").toLowerCase().includes(t));
}

/* Cuántas son y cuánto suman las que están a la vista.
 *
 * NO SUMA MONEDAS DISTINTAS. Con una factura en dólares y tres en pesos, un
 * único total es un número equivocado con cara de correcto — y es el número
 * que alguien va a copiar. Cuando hay más de una moneda se devuelve
 * `variasMonedas` y la pantalla dice eso en vez de un total.
 *
 * `sinImporte` son las que no tienen número: las que no se leyeron, las que no
 * se pudieron leer y los remitos, que no suelen traerlo. Se cuentan aparte
 * porque un total sobre la mitad de la lista, mostrado solo, se lee como el
 * total de toda la lista.
 */
export function resumen(filas) {
  const lista = filas || [];
  const monedas = new Set();
  let total = 0;
  let conImporte = 0;
  for (const f of lista) {
    const n = Number(f?.importe);
    if (f?.importe === null || f?.importe === undefined || !Number.isFinite(n)) {
      continue;
    }
    monedas.add(String(f?.moneda || "ARS").toUpperCase());
    total += n;
    conImporte += 1;
  }
  const variasMonedas = monedas.size > 1;
  return {
    cuantas: lista.length,
    conImporte,
    sinImporte: lista.length - conImporte,
    variasMonedas,
    moneda: monedas.size === 1 ? [...monedas][0] : null,
    total: conImporte > 0 && !variasMonedas ? total : null,
  };
}
